import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Timesheet, 
  TimesheetEntry,
  PaginationParams 
} from '@/types';
import { 
  useFetch, 
  usePaginatedFetch, 
} from './useApi';
import { apiClient } from '@/lib/api';

// Query keys
const TIMESHEETS_KEY = 'timesheets';

// ==================== Timesheets ====================

export function useTimesheets(params?: { status?: string; employeeId?: string } & PaginationParams) {
  return usePaginatedFetch<{ data: Timesheet[]; meta: { total: number } }>(
    TIMESHEETS_KEY,
    '/payroll/timesheets',
    {
      page: params?.page || 1,
      limit: params?.limit || 20,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    },
    {
      status: params?.status,
      employeeId: params?.employeeId,
    }
  );
}

export function useMyTimesheets(params?: { status?: string } & PaginationParams) {
  return usePaginatedFetch<{ data: Timesheet[]; meta: { total: number } }>(
    `${TIMESHEETS_KEY}-my`,
    '/payroll/timesheets/my',
    {
      page: params?.page || 1,
      limit: params?.limit || 20,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
    },
    {
      status: params?.status,
    }
  );
}

export function useTimesheet(id: string) {
  return useFetch<Timesheet>(
    [TIMESHEETS_KEY, id],
    `/payroll/timesheets/${id}`,
    undefined,
    { enabled: !!id }
  );
}

export function useTimesheetForWeek(weekStarting: string) {
  return useFetch<Timesheet>(
    [TIMESHEETS_KEY, 'week', weekStarting],
    `/payroll/timesheets/week/${weekStarting}`,
    undefined,
    { enabled: !!weekStarting }
  );
}

export function useCreateTimesheet() {
  const queryClient = useQueryClient();
  
  return useMutation<Timesheet, Error, { weekStarting: string; entries?: Partial<TimesheetEntry>[] }>({
    mutationFn: (data) => apiClient.post('/payroll/timesheets', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TIMESHEETS_KEY] });
    },
  });
}

export function useUpdateTimesheet() {
  const queryClient = useQueryClient();
  
  return useMutation<Timesheet, Error, { id: string; data: Partial<Timesheet> }>({
    mutationFn: ({ id, data }) => apiClient.put(`/payroll/timesheets/${id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [TIMESHEETS_KEY] });
      queryClient.invalidateQueries({ queryKey: [TIMESHEETS_KEY, variables.id] });
    },
  });
}

export function useSubmitTimesheet() {
  const queryClient = useQueryClient();
  
  return useMutation<Timesheet, Error, string>({
    mutationFn: (id) => apiClient.patch(`/payroll/timesheets/${id}/submit`, {}),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: [TIMESHEETS_KEY] });
      queryClient.invalidateQueries({ queryKey: [TIMESHEETS_KEY, id] });
    },
  });
}

export function useApproveTimesheet() {
  const queryClient = useQueryClient();
  
  return useMutation<Timesheet, Error, { id: string; approved: boolean; reason?: string }>({
    mutationFn: ({ id, approved, reason }) => 
      apiClient.patch(`/payroll/timesheets/${id}/approve`, { approved, reason }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [TIMESHEETS_KEY] });
      queryClient.invalidateQueries({ queryKey: [TIMESHEETS_KEY, variables.id] });
    },
  });
}

export function useAddTimesheetEntry() {
  const queryClient = useQueryClient();
  
  return useMutation<TimesheetEntry, Error, { timesheetId: string; data: Partial<TimesheetEntry> }>({
    mutationFn: ({ timesheetId, data }) => 
      apiClient.post(`/payroll/timesheets/${timesheetId}/entries`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [TIMESHEETS_KEY] });
      queryClient.invalidateQueries({ queryKey: [TIMESHEETS_KEY, variables.timesheetId] });
    },
  });
}

export function useUpdateTimesheetEntry() {
  const queryClient = useQueryClient();
  
  return useMutation<TimesheetEntry, Error, { 
    timesheetId: string; 
    entryId: string; 
    data: Partial<TimesheetEntry> 
  }>({
    mutationFn: ({ timesheetId, entryId, data }) => 
      apiClient.put(`/payroll/timesheets/${timesheetId}/entries/${entryId}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [TIMESHEETS_KEY] });
      queryClient.invalidateQueries({ queryKey: [TIMESHEETS_KEY, variables.timesheetId] });
    },
  });
}

export function useDeleteTimesheetEntry() {
  const queryClient = useQueryClient();
  
  return useMutation<void, Error, { timesheetId: string; entryId: string }>({
    mutationFn: ({ timesheetId, entryId }) => 
      apiClient.delete(`/payroll/timesheets/${timesheetId}/entries/${entryId}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [TIMESHEETS_KEY] });
      queryClient.invalidateQueries({ queryKey: [TIMESHEETS_KEY, variables.timesheetId] });
    },
  });
}
